import { supabase } from '../lib/supabase';

export const foodService = {
  // ============================================
  // CITIES
  // ============================================
  async getActiveCities() {
    const { data, error } = await supabase?.from('cities_food')?.select('*')?.eq('active', true)?.order('name');

    if (error) throw error;
    return data || [];
  },

  async getCityByName(cityName) {
    const { data, error } = await supabase?.from('cities_food')?.select('*')?.eq('name', cityName)?.eq('active', true)?.single();

    if (error) throw error;
    return data;
  },

  // ============================================
  // VENDORS
  // ============================================
  async getVendorsByCity(cityId) {
    const { data, error } = await supabase?.from('vendors_food')?.select('*')?.eq('city_id', cityId)?.eq('active', true)?.order('name');

    if (error) throw error;
    return data || [];
  },

  async getVendorById(vendorId) {
    const { data, error } = await supabase?.from('vendors_food')?.select('*')?.eq('id', vendorId)?.eq('active', true)?.single();

    if (error) throw error;
    return data;
  },

  async getVendorBySlug(slug) {
    const { data, error } = await supabase?.from('vendors_food')?.select('*')?.eq('slug', slug)?.eq('active', true)?.single();

    if (error) throw error;
    return data;
  },

  // ============================================
  // PRODUCTS
  // ============================================
  async getProductsByVendor(vendorId) {
    const { data, error } = await supabase?.from('products_food')?.select('*')?.eq('vendor_id', vendorId)?.eq('available', true)?.order('position');

    if (error) throw error;
    
    // Convert price from string to number for all products
    const products = (data || [])?.map(product => ({
      ...product,
      price: parseFloat(product?.price) || 0
    }));
    
    return products;
  },

  // ============================================
  // DELIVERY SLOTS
  // ============================================
  async getDeliverySlotsByCity(cityId) {
    const { data, error } = await supabase?.from('delivery_slots')?.select('*')?.eq('city_id', cityId)?.eq('active', true)?.order('start_time');

    if (error) throw error;
    return data || [];
  },

  // ============================================
  // ORDERS
  // ============================================
  async getOrders(filters = {}) {
    let query = supabase?.from('orders')?.select(`
      *,
      city:cities_food(id, name),
      vendor:vendors_food(id, name, phone)
    `);

    // Apply filters
    if (filters?.cityId) {
      query = query?.eq('city_id', filters?.cityId);
    }
    if (filters?.vendorId) {
      query = query?.eq('vendor_id', filters?.vendorId);
    }
    if (filters?.orderStatus) {
      query = query?.eq('order_status', filters?.orderStatus);
    }
    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async updateOrderStatus(orderId, newStatus) {
    const { data, error } = await supabase?.from('orders')?.update({ order_status: newStatus })?.eq('id', orderId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async createOrder(orderData) {
    const { data, error } = await supabase?.from('orders')?.insert({
        city_id: orderData?.cityId,
        vendor_id: orderData?.vendorId,
        customer_name: orderData?.customerName,
        customer_phone: orderData?.customerPhone,
        delivery_address: orderData?.deliveryAddress,
        delivery_slot: orderData?.deliverySlot,
        products_total: orderData?.productsTotal,
        delivery_fee: orderData?.deliveryFee,
        order_total: orderData?.orderTotal,
        payment_status: orderData?.paymentStatus || 'pending',
        stripe_payment_id: orderData?.stripePaymentId || null,
        notes: orderData?.notes || null,
        order_status: 'nuovo'
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  async updateOrderPaymentStatus(orderId, paymentStatus, stripePaymentId) {
    const { data, error } = await supabase?.from('orders')?.update({
        payment_status: paymentStatus,
        stripe_payment_id: stripePaymentId
      })?.eq('id', orderId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async updateOrderNotificationStatus(orderId, sent) {
    const { data, error } = await supabase?.from('orders')?.update({ notification_sent: sent })?.eq('id', orderId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getOrderById(orderId) {
    const { data, error } = await supabase?.from('orders')?.select(`
        *,
        city:cities_food(*),
        vendor:vendors_food(*)
      `)?.eq('id', orderId)?.single();

    if (error) throw error;
    return data;
  },

  // ============================================
  // ORDER ITEMS
  // ============================================
  async createOrderItems(orderId, items) {
    const orderItems = items?.map(item => ({
      order_id: orderId,
      product_name: item?.name,
      quantity: item?.quantity,
      unit_price: item?.price,
      line_total: item?.price * item?.quantity
    }));

    const { data, error } = await supabase?.from('order_items')?.insert(orderItems)?.select();

    if (error) throw error;
    return data;
  },

  async getOrderItems(orderId) {
    const { data, error } = await supabase?.from('order_items')?.select('*')?.eq('order_id', orderId)?.order('created_at');

    if (error) throw error;
    return data || [];
  },

  // ============================================
  // CART HELPERS (CLIENT-SIDE STORAGE)
  // ============================================
  getCart() {
    const cart = localStorage.getItem('food_cart');
    return cart ? JSON.parse(cart) : { items: [], vendorId: null, cityId: null };
  },

  saveCart(cart) {
    localStorage.setItem('food_cart', JSON.stringify(cart));
  },

  clearCart() {
    localStorage.removeItem('food_cart');
  },

  addToCart(product, vendorId, cityId) {
    // CRITICAL DEBUG: Log product and price at click
    console.log('🛒 ADD TO CART - Product received:', product);
    console.log('🛒 ADD TO CART - product.price:', product?.price, 'type:', typeof product?.price);
    console.log('🛒 ADD TO CART - product.price_eur:', product?.price_eur);
    console.log('🛒 ADD TO CART - product.prezzo:', product?.prezzo);
    
    const cart = this.getCart();
    
    // If cart has items from different vendor, clear it
    if (cart?.vendorId && cart?.vendorId !== vendorId) {
      cart.items = [];
    }
    
    cart.vendorId = vendorId;
    cart.cityId = cityId;
    
    // CRITICAL FIX: Multiple fallback fields for price with NaN blocking
    const rawPrice = product?.price ?? product?.price_eur ?? product?.prezzo ?? 0;
    const numericPrice = Number(rawPrice);
    
    console.log('🛒 ADD TO CART - rawPrice:', rawPrice, 'numericPrice:', numericPrice);
    
    // BLOCK if price is NaN or invalid
    if (isNaN(numericPrice) || numericPrice <= 0) {
      console.error('❌ PREZZO NON VALIDO - Impossibile aggiungere al carrello');
      console.error('Product data:', product);
      alert('Errore: Prezzo non valido per questo prodotto. Impossibile aggiungere al carrello.');
      return cart;
    }
    
    const existingItem = cart?.items?.find(item => item?.id === product?.id);
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      // Store product with guaranteed numeric price
      const productWithNumericPrice = {
        ...product,
        price: numericPrice, // ALWAYS numeric, never string/null
        quantity: 1
      };
      console.log('🛒 ADD TO CART - Saving to cart:', productWithNumericPrice);
      cart?.items?.push(productWithNumericPrice);
    }
    
    this.saveCart(cart);
    console.log('🛒 ADD TO CART - Cart after save:', cart);
    return cart;
  },

  removeFromCart(productId) {
    const cart = this.getCart();
    cart.items = cart?.items?.filter(item => item?.id !== productId);
    this.saveCart(cart);
    return cart;
  },

  updateCartItemQuantity(productId, quantity) {
    const cart = this.getCart();
    const item = cart?.items?.find(item => item?.id === productId);
    if (item) {
      if (quantity <= 0) {
        cart.items = cart?.items?.filter(item => item?.id !== productId);
      } else {
        item.quantity = quantity;
      }
    }
    this.saveCart(cart);
    return cart;
  },

  calculateCartTotals(cartItems = null, deliveryFee = 3.00) {
    const items = cartItems || this.getCart()?.items || [];

    const productsTotal = items?.reduce((sum, item) => {
      const itemPrice = parseFloat(item?.price) || 0;
      const itemQuantity = parseInt(item?.quantity) || 0;
      const lineTotal = itemPrice * itemQuantity;
      return sum + lineTotal;
    }, 0);

    const orderTotal = productsTotal + deliveryFee;
    const itemCount = items?.reduce((count, item) => count + (parseInt(item?.quantity) || 0), 0);

    return {
      productsTotal,
      deliveryFee,
      orderTotal,
      itemCount
    };
  }
};